import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const APIPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const endpoints = [
    {
      method: 'GET',
      endpoint: '/api/v1/programs',
      description: 'Retrieve all residency programs',
      params: ['specialty', 'location', 'img_friendly']
    },
    {
      method: 'POST',
      endpoint: '/api/v1/match',
      description: 'Get personalized program matches',
      params: ['profile_data', 'preferences']
    },
    {
      method: 'GET',
      endpoint: '/api/v1/programs/{id}',
      description: 'Get detailed program information',
      params: ['include_stats', 'include_requirements']
    },
    {
      method: 'POST',
      endpoint: '/api/v1/analyze',
      description: 'Analyze application probability',
      params: ['scores', 'experience', 'target_programs']
    }
  ];

  const codeExamples = {
    javascript: `
// Initialize the API client
const residencyAPI = new ResidencyCompanionAPI({
  apiKey: 'your-api-key',
  version: 'v1'
});

// Get program matches
const matches = await residencyAPI.getMatches({
  step1: 245,
  step2: 260,
  specialty: 'internal-medicine',
  visaStatus: 'h1b',
  location: ['new-york', 'california']
});

console.log(matches);`,
    python: `
import residency_companion

# Initialize client
client = residency_companion.Client(api_key='your-api-key')

# Get program matches
matches = client.get_matches({
    'step1': 245,
    'step2': 260,
    'specialty': 'internal-medicine',
    'visa_status': 'h1b',
    'location': ['new-york', 'california']
})

print(matches)`,
    curl: `
curl -X POST https://api.residencycompanion.com/v1/match \\
  -H "Authorization: Bearer your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "step1": 245,
    "step2": 260,
    "specialty": "internal-medicine",
    "visa_status": "h1b",
    "location": ["new-york", "california"]
  }'`
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
<Link to="/" className="flex items-center">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-10 h-10 mr-4 object-contain"
/>
  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
    Residency Companion
  </h1>
</Link>
            <div className="flex items-center space-x-1">
              <button className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get API Key
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-4 h-4 text-blue-600">⚡</span>
              <span className="text-sm text-gray-700 font-medium">RESTful API for Developers</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              Powerful
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                API
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              Integrate residency matching intelligence into your applications with our comprehensive API
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'endpoints', 'examples', 'authentication'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap py-2 px-4 rounded-lg font-medium transition-all duration-300 ${
                  activeTab === tab 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-12">
              <div className="text-center">
                <h2 className="text-4xl font-thin text-gray-900 mb-6">API Overview</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                  Build powerful medical education applications with our residency matching API
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl">
                  <div className="text-3xl mb-4">🚀</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Fast & Reliable</h3>
                  <p className="text-gray-600">99.9% uptime with sub-100ms response times globally</p>
                </div>
                
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-8 rounded-2xl">
                  <div className="text-3xl mb-4">🔒</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Secure</h3>
                  <p className="text-gray-600">Enterprise-grade security with API key authentication</p>
                </div>
                
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-8 rounded-2xl">
                  <div className="text-3xl mb-4">📊</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Comprehensive</h3>
                  <p className="text-gray-600">Access to 600+ programs with detailed analytics</p>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-600">Real-time program matching</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-600">Probability analysis algorithms</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-600">Comprehensive program database</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <span className="text-gray-600">RESTful design principles</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Endpoints Tab */}
          {activeTab === 'endpoints' && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-4xl font-thin text-gray-900 mb-6">API Endpoints</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                  Complete reference for all available API endpoints
                </p>
              </div>

              <div className="space-y-6">
                {endpoints.map((endpoint, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                      <div className="flex items-center space-x-4 mb-2 lg:mb-0">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          endpoint.method === 'GET' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {endpoint.method}
                        </span>
                        <code className="text-lg font-mono text-gray-900">{endpoint.endpoint}</code>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">{endpoint.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {endpoint.params.map((param, paramIndex) => (
                        <span key={paramIndex} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                          {param}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Examples Tab */}
          {activeTab === 'examples' && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-4xl font-thin text-gray-900 mb-6">Code Examples</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                  Get started quickly with these code examples
                </p>
              </div>

              <div className="space-y-8">
                {Object.entries(codeExamples).map(([language, code]) => (
                  <div key={language} className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="bg-gray-800 px-6 py-3 border-b border-gray-700">
                      <h3 className="text-white font-medium capitalize">{language}</h3>
                    </div>
                    <pre className="p-6 text-gray-300 overflow-x-auto">
                      <code>{code.trim()}</code>
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Authentication Tab */}
          {activeTab === 'authentication' && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-4xl font-thin text-gray-900 mb-6">Authentication</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                  Secure your API requests with proper authentication
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">API Key Authentication</h3>
                    <p className="text-gray-600 mb-4">
                      Include your API key in the Authorization header of every request:
                    </p>
                    <code className="bg-gray-900 text-gray-300 p-4 rounded block">
                      Authorization: Bearer your-api-key
                    </code>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Rate Limits</h3>
                    <div className="space-y-2 text-gray-600">
                      <div>• Free tier: 100 requests/hour</div>
                      <div>• Pro tier: 1,000 requests/hour</div>
                      <div>• Enterprise: Custom limits</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-yellow-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Getting Started</h3>
                    <ol className="space-y-2 text-gray-600">
                      <li>1. Sign up for an account</li>
                      <li>2. Generate your API key</li>
                      <li>3. Make your first request</li>
                      <li>4. Build amazing applications!</li>
                    </ol>
                  </div>

                  <div className="bg-red-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Best Practices</h3>
                    <div className="space-y-2 text-gray-600">
                      <div>• Never expose API keys in client-side code</div>
                      <div>• Use environment variables</div>
                      <div>• Rotate keys regularly</div>
                      <div>• Monitor usage patterns</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
            Ready to Build?
          </h2>
          <p className="text-xl text-blue-100 mb-8 font-light">
            Get your API key and start building in minutes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
              Get API Key
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-blue-600">
              View Documentation
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-12 h-12 mr-4 object-contain"
/>
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">Medical College Guidance Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your partner in medical education decisions.
              </p>
            </div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/features" className="hover:text-blue-600 transition-colors">Features</Link></li>
    <li><Link to="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</Link></li>
    <li><Link to="/api" className="text-blue-600 font-medium">API</Link></li>
  </ul>
</div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/help" className="hover:text-blue-600 transition-colors">Help Center</Link></li>
    <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
    <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
    <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
    <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link></li>
    <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
  </ul>
</div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
  </ul>
</div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Residency Companion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default APIPage;